// ai_ui_v1 — ผู้ช่วย AI: ตั้งค่า AI ต่อธุรกิจ (kb_set) + จัดการความรู้ (RAG) + โทเคน/การใช้งาน
import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { Bot, Save, Trash2, Plus, Sparkles, Database, Wallet, BarChart3, Loader, X, Upload, CreditCard, Check, Send, AlertTriangle, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';

const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 };
const btn = (bg, disabled) => ({
  background: disabled ? '#cbd5e1' : bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 18px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14,
  display: 'inline-flex', alignItems: 'center', gap: 6,
});
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const lbl = { fontWeight: 700, fontSize: 13, color: '#374151', marginBottom: 6, display: 'block' };

const MODES = [
  { v: 'off', t: '🚫 ปิด AI', d: 'ไม่ตอบอัตโนมัติ' },
  { v: 'keyword', t: '🔑 คีย์เวิร์ด (เดิม)', d: 'ตอบจากคำที่ตั้งไว้ล่วงหน้า ฟรี' },
  { v: 'rag', t: '📚 AI + ฐานความรู้ (แนะนำ)', d: 'ตอบด้วย AI อ้างอิงความรู้ของธุรกิจนี้เท่านั้น' },
  { v: 'llm', t: '🧠 AI อิสระ', d: 'ตอบด้วย AI ทั่วไป (ไม่จำกัดความรู้)' },
];
const nf = (n) => Number(n || 0).toLocaleString();
const CH_ICON = { line: '💚', telegram: '✈️', facebook: '💙', whatsapp: '💬', instagram: '📷', webchat: '🌐' }; /* ai_channels_v1 */

// kb_template_v1 — starter templates (1 หัวข้อ = 1 เอกสาร เพื่อให้ RAG ค้นแม่น)
const TEMPLATES = [
  {
    short: '🏪 ข้อมูลทั่วไป', title: 'ข้อมูลทั่วไป',
    text: 'ชื่อร้าน/ธุรกิจ: \nธุรกิจเกี่ยวกับ: (ขายอะไร/บริการอะไร อธิบาย 1-2 ประโยค)\nเวลาเปิด-ปิด: เปิดทุกวัน เวลา __:__ ถึง __:__ น.\nช่องทางติดต่อ: LINE: __ / โทร: __\nพื้นที่ให้บริการ/ที่อยู่: \nจุดเด่นของร้าน: (เช่น ส่งไว ของแท้ มีแอดมินดูแล 24 ชม.)',
  },
  {
    short: '🛍️ สินค้า/ราคา', title: 'สินค้าและราคา',
    text: 'รายการสินค้า/บริการ:\n1. ชื่อ: __ ราคา: __ บาท รายละเอียด: __\n2. ชื่อ: __ ราคา: __ บาท รายละเอียด: __\n3. ชื่อ: __ ราคา: __ บาท รายละเอียด: __\nขั้นต่ำในการสั่งซื้อ: __ บาท\nสินค้าขายดี/แนะนำ: \nสิ่งที่ร้านไม่มี/ไม่รับทำ: (สำคัญ กัน AI ตอบเกินจริง)',
  },
  {
    short: '💳 สั่งซื้อ/ชำระเงิน', title: 'วิธีสั่งซื้อและชำระเงิน',
    text: 'ขั้นตอนการสั่งซื้อ:\n1. \n2. \n3. \nช่องทางชำระเงิน: โอนธนาคาร __ / PromptPay: __\nชื่อบัญชี: \nหลังโอนแล้ว: (เช่น ส่งสลิปในแชท รอตรวจสอบ 1-5 นาที)\nระยะเวลาดำเนินการ: (เช่น เติมเสร็จภายใน 1 นาที / จัดส่งภายใน 24 ชม.)',
  },
  {
    short: '🚚 จัดส่ง/นโยบาย', title: 'การจัดส่งและนโยบาย',
    text: 'การจัดส่ง: (ขนส่งอะไร ค่าส่งเท่าไหร่ ส่งฟรีเมื่อซื้อครบ __ บาท)\nระยะเวลาจัดส่ง: \nเปลี่ยน/คืนสินค้า: ได้ภายใน __ วัน เงื่อนไข: \nการรับประกัน: \nกรณีมีปัญหา: (เช่น แจ้งแอดมินพร้อมรูปและเลขออเดอร์)',
  },
  {
    short: '❓ FAQ', title: 'คำถามที่พบบ่อย',
    text: 'ถาม: \nตอบ: \n\nถาม: \nตอบ: \n\nถาม: \nตอบ: \n\n(เขียนแบบถาม-ตอบ ช่วยให้ AI จับคู่คำถามลูกค้าได้แม่น แนะนำ 5-15 ข้อ)',
  },
];

// kb_template_game_v1 — vertical pack for gaming/topup sites (built from real top questions:
// ฝาก-ถอน-เติม 15.8k, เงินไม่เข้า/ปัญหา 8.2k, โปร/โบนัส 1.6k, สมัคร/ลืมรหัส 1.4k, ทีเด็ด/เกมแตก)
const TEMPLATES_GAME = [
  {
    short: '💰 ฝาก-ถอน', title: 'การฝาก-ถอนเงิน',
    text: 'ฝากเงิน:\n- ธนาคารที่รับ: __ (เลขบัญชี __ / ชื่อบัญชี __)\n- ฝากขั้นต่ำ: __ บาท\n- วิธีฝาก: Login → เมนูฝากเงิน → เลือกธนาคาร → โอน → แจ้งสลิป\nถอนเงิน:\n- ถอนขั้นต่ำ: __ บาท / สูงสุด __ บาทต่อครั้ง\n- ถอนได้ __ ครั้ง/วัน · ใช้เวลา __ นาที\n- เงื่อนไข: (เช่น ต้องมียอดเทิร์นโอเวอร์ __ เท่า)',
  },
  {
    short: '⚠️ เงินไม่เข้า/แก้ปัญหา', title: 'เงินไม่เข้า / ฝาก-ถอนมีปัญหา',
    text: 'ถาม: เงินไม่เข้า / ยอดไม่เข้า / ฝากแล้วไม่เข้า\nตอบ: รบกวนส่ง (1) สลิปโอน (2) ยูสเซอร์ (3) เวลาที่โอน มาให้แอดมินตรวจสอบนะคะ ยอดจะเข้าภายใน __ นาที\n\nถาม: ฝากเงินไม่ได้ / โอนแล้วระบบไม่ขึ้น\nตอบ: __ (เช่น ตรวจสอบว่าโอนตรงเลขบัญชี+ชื่อบัญชีล่าสุดที่แสดงในระบบ)\n\nถาม: โอนผิดบัญชี / ถอนไม่ได้\nตอบ: __\n\n(หมายเหตุ: ตั้งคำ "เงินไม่เข้า, ถอนไม่ได้, โอนผิด" ในช่อง 🚨 หัวข้อแจ้งแอดมิน ด้วย)',
  },
  {
    short: '🔑 สมัคร/ลืมรหัส', title: 'สมัครสมาชิก / เข้าระบบ',
    text: 'สมัครสมาชิก: __ (เช่น สมัครฟรีที่ลิงก์ __ / แจ้งแอดมินสมัครให้)\nลิงก์เข้าเล่น: __\nลืมรหัสผ่าน: แจ้งยูสเซอร์ + เบอร์โทร → แอดมินรีเซ็ตให้ภายใน __ นาที\nเข้าระบบไม่ได้: __',
  },
  {
    short: '🎁 โปรโมชั่น/โบนัส', title: 'โปรโมชั่นและโบนัส',
    text: 'โปรโมชั่นตอนนี้: __\nโบนัสฝากแรก: __ %\nโบนัสรายวัน / คืนยอดเสีย: __\nเครดิตฟรี + เงื่อนไข: __\nกิจกรรม/กงล้อ/แนะนำเพื่อน: __',
  },
  {
    short: '🎰 ทีเด็ด/เกมแตก', title: 'ทีเด็ด / เกมแตก / ค่ายเกม',
    text: 'เกม/ค่ายแนะนำวันนี้: __\nค่ายเกมที่มี: __ (เช่น PG, JOKER, สล็อต, บาคาร่า)\nวิธีเข้าเล่น: __\n\n(หมายเหตุ: ใส่รูปเกมใน "คลังรูปสุ่ม" + ตั้งคำ "ทีเด็ด, เกมแตก, BONUS, บาคาร่า" ในช่อง 🎯 คำที่ให้ส่งรูปเสมอ → AI จะส่งการ์ดเกมอัตโนมัติ)',
  },
];

export default function AIAssistant() {
  const [biz, setBiz] = useState([]);
  const [channels, setChannels] = useState([]); /* ai_channels_v1 */
  const [models, setModels] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [usage, setUsage] = useState([]);
  const [sel, setSel] = useState(null); // selected kb_set id
  const [cfg, setCfg] = useState(null);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState('config'); // config | knowledge | usage
  const [saving, setSaving] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: '', text: '', image_url: '' });
  const [ingesting, setIngesting] = useState(false);
  const [imgUploading, setImgUploading] = useState(false); /* ai_kbimage_v1 */
  // ai_gallery_v1
  const [galleryImgs, setGalleryImgs] = useState([]);
  const [galTitle, setGalTitle] = useState('');
  const [galUploading, setGalUploading] = useState(false);
  // ai_kbedit_v1: editing an existing document
  const [editDocId, setEditDocId] = useState(null);
  // ai_ingest_v1: URL / file ingest
  const [ingestUrl, setIngestUrl] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  // ai_unanswered_v1
  const [unans, setUnans] = useState([]);
  const [resolveId, setResolveId] = useState(null);
  // ai_test_v1: playground
  const [testMsgs, setTestMsgs] = useState([]);
  const [testQ, setTestQ] = useState('');
  const [testing, setTesting] = useState(false);
  // token purchase
  const [showBuy, setShowBuy] = useState(false);
  const [pkgs, setPkgs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [buyPkg, setBuyPkg] = useState(null);
  const [slip, setSlip] = useState(null);
  const [buying, setBuying] = useState(false);

  const openBuy = async () => {
    setShowBuy(true); setBuyPkg(null); setSlip(null);
    try {
      const [p, b, o] = await Promise.all([
        API.get('/api/ai/token-packages'), API.get('/api/me/bank-accounts'), API.get('/api/ai/my-token-orders'),
      ]);
      setPkgs(p.data); setBanks(b.data.bank_accounts || b.data || []); setOrders(o.data);
    } catch (e) { toast.error('โหลดข้อมูลไม่สำเร็จ'); }
  };
  const submitBuy = async () => {
    if (!buyPkg) return toast.error('กรุณาเลือกแพ็กเกจ');
    if (!slip) return toast.error('กรุณาอัปโหลดสลิป');
    setBuying(true);
    try {
      const fd = new FormData();
      fd.append('package_id', buyPkg.id); fd.append('slip', slip);
      await API.post('/api/ai/buy-tokens', fd);
      toast.success('ส่งคำสั่งซื้อแล้ว รอแอดมินอนุมัติ');
      setBuyPkg(null); setSlip(null);
      const o = await API.get('/api/ai/my-token-orders'); setOrders(o.data);
    } catch (e) { toast.error(e.response?.data?.error || 'ซื้อไม่สำเร็จ'); }
    setBuying(false);
  };

  const loadTop = useCallback(async () => {
    try {
      const [b, m, w, u, ch] = await Promise.all([
        API.get('/api/ai/businesses'), API.get('/api/ai/models'),
        API.get('/api/ai/wallet'), API.get('/api/ai/usage'),
        API.get('/api/channels/all'), /* ai_channels_v1 */
      ]);
      setBiz(b.data); setModels(m.data); setWallet(w.data); setUsage(u.data);
      setChannels(Array.isArray(ch.data) ? ch.data : []);
      if (!sel && b.data[0]) setSel(b.data[0].id);
    } catch (e) { toast.error('โหลดข้อมูลไม่สำเร็จ'); }
  }, [sel]);

  useEffect(() => { loadTop(); }, []); // eslint-disable-line

  const loadBiz = useCallback(async (id) => {
    if (!id) return;
    try {
      const [c, d, un, g] = await Promise.all([
        API.get(`/api/ai/config/${id}`), API.get(`/api/ai/documents/${id}`),
        API.get(`/api/ai/unanswered?kbSetId=${id}`), /* ai_unanswered_v1 */
        API.get(`/api/ai/gallery/${id}`), /* ai_gallery_v1 */
      ]);
      setCfg(c.data); setDocs(d.data); setUnans(un.data); setGalleryImgs(g.data);
    } catch (e) { toast.error('โหลดธุรกิจไม่สำเร็จ'); }
  }, []);
  useEffect(() => { if (sel) { loadBiz(sel); setTestMsgs([]); setEditDocId(null); setNewDoc({ title: '', text: '', image_url: '' }); } }, [sel, loadBiz]);

  // ai_test_v1: ask the bot as if we were a customer (real pipeline, real tokens, nothing sent to chats)
  const runTest = async () => {
    const q = testQ.trim();
    if (!q || testing) return;
    setTestQ('');
    setTestMsgs((m) => [...m, { role: 'user', text: q }]);
    setTesting(true);
    try {
      const { data: d } = await API.post(`/api/ai/test/${sel}`, { question: q });
      /* kb_match_trace_v1: เก็บรายการที่ตรงไว้ด้วย เพื่อลิงก์ไปแก้ได้ตรงจุด */
      setTestMsgs((m) => [...m, { role: 'bot', text: d.answer || '(บอทไม่ตอบข้อความนี้ — ระบบจะแจ้งเตือนแอดมินแทน)', source: d.source, alert: d.needAlert, matchedKb: d.matchedKb || null }]);
      loadTop(); // refresh wallet after real usage
    } catch (e) {
      setTestMsgs((m) => [...m, { role: 'bot', text: e.response?.data?.error || 'ทดสอบไม่สำเร็จ', source: 'error' }]);
    }
    setTesting(false);
  };

  const providers = [...new Set(models.map((m) => m.provider))];
  const modelsFor = (p) => models.filter((m) => m.provider === p);
  const PROV_LABEL = { anthropic: 'Claude (ไทยเนียน)', gemini: 'Gemini (ถูก/เร็ว)', typhoon: 'Typhoon (ไทย)' };

  const setCfgField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const body = {
        ai_mode: cfg.ai_mode,
        ai_provider: cfg.ai_provider || null,
        ai_chat_model: cfg.ai_chat_model || null,
        ai_prompt: cfg.ai_prompt || null,
        ai_rag_enabled: cfg.ai_mode === 'rag',
        ai_temperature: cfg.ai_temperature,
        ai_fallback_answer: cfg.ai_fallback_answer || null,
        ai_alert_topics: cfg.ai_alert_topics || null, /* ai_alert_topics_v1 */
        ai_deescalation: cfg.ai_deescalation !== false, /* ai_deescalation_v1 */
        ai_gallery_count: cfg.ai_gallery_count || 3, /* ai_gallery_v1 */
        ai_gallery_topics: cfg.ai_gallery_topics || null, /* ai_gallery_topics_v1 */
        ai_gallery_cta: cfg.ai_gallery_cta || null, /* ai_gallery_cta_v1 */
        ai_gallery_link: cfg.ai_gallery_link || null,
        ai_gallery_badge_text: cfg.ai_gallery_badge_text || null, /* ai_gallery_badge_v1 */
        ai_gallery_pct_min: cfg.ai_gallery_pct_min == null ? 90 : cfg.ai_gallery_pct_min,
        /* ai_gallery_card_v2 */
        ai_gallery_subtitle: cfg.ai_gallery_subtitle || null,
        ai_gallery_stat2_label: cfg.ai_gallery_stat2_label || null,
        ai_gallery_stat2_value: cfg.ai_gallery_stat2_value || null,
        ai_gallery_stat3_label: cfg.ai_gallery_stat3_label || null,
        ai_gallery_stat3_value: cfg.ai_gallery_stat3_value || null,
        /* gallery_trust_row_v1 */
        ai_gallery_trust1_url: cfg.ai_gallery_trust1_url || null,
        ai_gallery_trust1_label: cfg.ai_gallery_trust1_label || null,
        ai_gallery_trust2_url: cfg.ai_gallery_trust2_url || null,
        ai_gallery_trust2_label: cfg.ai_gallery_trust2_label || null,
        ai_gallery_trust3_url: cfg.ai_gallery_trust3_url || null,
        ai_gallery_trust3_label: cfg.ai_gallery_trust3_label || null,
        ai_gallery_pct_max: cfg.ai_gallery_pct_max == null ? 98 : cfg.ai_gallery_pct_max,
        channel_ids: Array.isArray(cfg.channel_ids) ? cfg.channel_ids : null, /* ai_channels_v1 */
      };
      await API.put(`/api/ai/config/${sel}`, body);
      toast.success('บันทึกการตั้งค่าแล้ว');
      loadTop();
    } catch (e) { toast.error(e.response?.data?.error || 'บันทึกไม่สำเร็จ'); }
    setSaving(false);
  };

  const addDoc = async () => {
    if (!newDoc.text.trim()) return toast.error('กรุณาใส่เนื้อหาความรู้');
    setIngesting(true);
    try {
      if (editDocId) {
        /* ai_kbedit_v1: save edits to an existing document (re-embed) */
        const r = await API.put(`/api/ai/document/${editDocId}`, newDoc);
        toast.success(`แก้ไขความรู้สำเร็จ (${r.data.chunks} ชิ้น)`);
        setEditDocId(null);
      } else {
        const r = await API.post(`/api/ai/documents/${sel}`, newDoc);
        toast.success(`เพิ่มความรู้สำเร็จ (${r.data.chunks} ชิ้น)`);
        /* ai_unanswered_v1: knowledge added from an unanswered question -> mark it resolved */
        if (resolveId) {
          API.post(`/api/ai/unanswered/${resolveId}/resolve`).catch(() => {});
          setUnans((a) => a.filter((x) => x.id !== resolveId));
          setResolveId(null);
        }
      }
      setNewDoc({ title: '', text: '', image_url: '' });
      loadBiz(sel); loadTop();
    } catch (e) { toast.error(e.response?.data?.error || 'บันทึกไม่สำเร็จ'); }
    setIngesting(false);
  };

  /* ai_kbedit_v1: load a document into the form for editing */
  const editDoc = async (d) => {
    try {
      const r = await API.get(`/api/ai/document/${d.id}`);
      setNewDoc({ title: r.data.title || '', text: r.data.raw_text || '', image_url: r.data.image_url || '' });
      setEditDocId(d.id);
      setResolveId(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast('แก้ไขแล้วกด "บันทึกการแก้ไข"', { icon: '✏️' });
    } catch (e) { toast.error(e.response?.data?.error || 'โหลดเอกสารไม่สำเร็จ'); }
  };
  const cancelEdit = () => { setEditDocId(null); setNewDoc({ title: '', text: '', image_url: '' }); };

  const delDoc = async (id) => {
    if (!window.confirm('ลบความรู้นี้?')) return;
    try { await API.delete(`/api/ai/documents/${id}`); toast.success('ลบแล้ว'); loadBiz(sel); loadTop(); }
    catch (e) { toast.error('ลบไม่สำเร็จ'); }
  };

  // kb_template_v1: fill the form from a starter template
  /* ai_ingest_v1: pull knowledge from a web page / uploaded file */
  const fetchFromUrl = async () => {
    const u = ingestUrl.trim();
    if (!u) return toast.error('วางลิงก์ก่อน');
    setUrlLoading(true);
    try {
      const r = await API.post(`/api/ai/documents/${sel}/from-url`, { url: u });
      toast.success(`ดึงจากเว็บสำเร็จ (${r.data.chunks} ชิ้น, ${nf(r.data.chars)} ตัวอักษร)`);
      setIngestUrl(''); loadBiz(sel); loadTop();
    } catch (e) { toast.error(e.response?.data?.error || 'ดึงไม่สำเร็จ'); }
    setUrlLoading(false);
  };
  const uploadKbFile = async (e) => {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    setFileLoading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await API.post(`/api/ai/documents/${sel}/upload`, fd);
      toast.success(`อ่านไฟล์สำเร็จ (${r.data.chunks} ชิ้น)`);
      loadBiz(sel); loadTop();
    } catch (e2) { toast.error(e2.response?.data?.error || 'อ่านไฟล์ไม่สำเร็จ'); }
    setFileLoading(false);
  };

  /* ai_gallery_v1: random-image gallery management (multi-upload) */
  const uploadGalleryImg = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    if (!files.length) return;
    setGalUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const fd = new FormData(); fd.append('file', files[i]);
        const up = await API.post('/api/upload', fd);
        const r = await API.post(`/api/ai/gallery/${sel}`, { image_url: up.data.url, title: galTitle.trim() || null });
        setGalleryImgs((a) => [r.data, ...a]); ok++;
      } catch (err) { toast.error(`อัปโหลด ${files[i].name} ไม่สำเร็จ`); }
    }
    if (ok) toast.success(`เพิ่มรูปเข้าคลังแล้ว ${ok} รูป`);
    setGalTitle('');
    setGalUploading(false);
  };
  /* gallery_trust_row_v1: อัปโหลดโลโก้สำหรับแถวท้ายการ์ด
     ใช้ตัวอัปโหลดกลางตัวเดิม (/api/upload) ที่เก็บไฟล์ไว้ใน /uploads แล้วคืน URL กลับมา
     ไม่ได้เข้าคลังรูปสุ่ม เพราะโลโก้ไม่ใช่รูปเกม ไม่ควรถูกสุ่มมาแสดงเป็นการ์ด */
  const [trustUploading, setTrustUploading] = useState(0);
  const uploadTrustLogo = async (slot, e) => {
    const file = (e.target.files || [])[0]; e.target.value = '';
    if (!file) return;
    setTrustUploading(slot);
    try {
      const fd = new FormData(); fd.append('file', file);
      const up = await API.post('/api/upload', fd);
      setCfgField('ai_gallery_trust' + slot + '_url', up.data.url);
      toast.success('อัปโหลดแล้ว — กดบันทึกเพื่อใช้งาน');
    } catch (err) { toast.error('อัปโหลดไม่สำเร็จ'); }
    setTrustUploading(0);
  };

  /* ai_gallery_cta_v1: per-image link (overrides the gallery default link) */
  const editGalleryLink = async (g) => {
    const url = window.prompt('ลิงก์เฉพาะรูปนี้ (เว้นว่าง = ใช้ลิงก์หลักของคลัง)', g.link_url || '');
    if (url === null) return;
    try {
      const r = await API.put(`/api/ai/gallery/${g.id}`, { link_url: url.trim() || null });
      setGalleryImgs((a) => a.map((x) => (x.id === g.id ? r.data : x)));
      toast.success('บันทึกลิงก์แล้ว');
    } catch (e) { toast.error(e.response?.data?.error || 'ไม่สำเร็จ'); }
  };
  const delGalleryImg = async (id) => {
    if (!window.confirm('ลบรูปนี้ออกจากคลัง?')) return;
    try { await API.delete(`/api/ai/gallery/${id}`); setGalleryImgs((a) => a.filter((x) => x.id !== id)); }
    catch (e) { toast.error('ลบไม่สำเร็จ'); }
  };

  /* ai_unanswered_v1: prefill knowledge form from an unanswered question */
  const addAsKnowledge = (u) => {
    setNewDoc({ title: u.question.slice(0, 80), text: 'ถาม: ' + u.question + '\nตอบ: ' });
    setResolveId(u.id);
    setTab('knowledge');
    toast('พิมพ์คำตอบต่อท้าย "ตอบ:" แล้วกดเพิ่มความรู้', { icon: '✍️' });
  };
  const dismissUnans = async (id) => {
    try { await API.post(`/api/ai/unanswered/${id}/dismiss`); setUnans((a) => a.filter((x) => x.id !== id)); }
    catch (e) { toast.error('ไม่สำเร็จ'); }
  };

  /* ai_kbimage_v1: attach an image to a knowledge doc (AI sends it with matching answers) */
  const uploadDocImage = async (e) => {
    const f = e.target.files[0]; e.target.value = '';
    if (!f) return;
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await API.post('/api/upload', fd);
      setNewDoc((d) => ({ ...d, image_url: r.data.url }));
      toast.success('แนบรูปแล้ว');
    } catch (err) { toast.error('อัปโหลดรูปไม่สำเร็จ'); }
    setImgUploading(false);
  };

  const applyTemplate = (t) => {
    if (newDoc.text.trim() && !window.confirm('แทนที่เนื้อหาที่พิมพ์อยู่ด้วยเทมเพลต?')) return;
    setNewDoc({ title: t.title, text: t.text, image_url: '' });
  };

  const selBiz = biz.find((b) => b.id === sel);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 24, marginBottom: 4 }}>
        <Sparkles size={26} color="#7c3aed" /> ผู้ช่วย AI
      </h1>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: 20 }}>ตั้งค่า AI ตอบแชท + ฐานความรู้ แยกตามแต่ละธุรกิจ</p>

      {/* wallet bar */}
      {wallet && (
        <div style={{ ...card, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', background: 'linear-gradient(90deg,#faf5ff,#eff6ff)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Wallet size={22} color="#7c3aed" />
            <div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>โทเคนคงเหลือ</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: wallet.balance > 0 ? '#7c3aed' : '#dc2626' }}>{nf(wallet.balance)}</div>
            </div>
          </div>
          <div><div style={{ fontSize: 12, color: '#6b7280' }}>ใช้ไปทั้งหมด</div><div style={{ fontWeight: 700 }}>{nf(wallet.used)}</div></div>
          <div><div style={{ fontSize: 12, color: '#6b7280' }}>รายเดือน / เติม</div><div style={{ fontWeight: 700 }}>{nf(wallet.monthly)} / {nf(wallet.topup)}</div></div>
          {wallet.trial > 0 && (
            <div style={{ padding: '4px 10px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#854d0e' }}>🎁 ทดลอง</div>
              <div style={{ fontWeight: 700, color: '#a16207' }}>{nf(wallet.trial)}</div>
              {wallet.trial_expires && <div style={{ fontSize: 11, color: '#a16207' }}>หมดอายุ {new Date(wallet.trial_expires).toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}</div>}
            </div>
          )}
          <button style={{ ...btn('#7c3aed'), marginLeft: 'auto' }} onClick={openBuy}>
            <Plus size={16} /> เติมโทเคน
          </button>
        </div>
      )}

      {/* ai_channels_v1: channels not bound to any business */}
      {(() => {
        const assigned = new Set(biz.flatMap((b) => b.channel_ids || []));
        const un = channels.filter((c) => !assigned.has(c.id));
        if (!un.length) return null;
        return (
          <div style={{ padding: '10px 14px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
            ⚠️ มี {un.length} ช่องทางยังไม่ถูกผูกกับธุรกิจใด: <b>{un.map((c) => c.name).join(', ')}</b> — ช่องเหล่านี้จะใช้ค่าเริ่มต้นของบัญชี (เลือกธุรกิจแล้วติ๊กช่องในแท็บ "ตั้งค่า AI")
          </div>
        );
      })()}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* business list */}
        <div style={{ ...card, width: 260, flexShrink: 0 }}>
          <div style={{ ...lbl, marginBottom: 12 }}>ธุรกิจ ({biz.length})</div>
          {/* ai_empty_link_v1: เดิมบอกให้ไปหน้า "สอน AI" ซึ่งไม่มีอยู่ในเมนู
              ผู้ใช้จึงหาไม่เจอว่าต้องเริ่มตรงไหน — ชี้ไปหน้าจริงและกดไปได้เลย
              (หน้านั้นเรียกสิ่งนี้ว่า "KB Set" จึงบอกชื่อทั้งสองแบบไว้ให้เชื่อมกันได้) */}
          {biz.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: 13, lineHeight: 1.7 }}>
              ยังไม่มีธุรกิจ<br />
              เริ่มที่หน้า{' '}
              <a href="/bot-training" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'underline' }}>เทรนบอท</a>
              {' '}แล้วกด <b>“+ สร้าง KB Set”</b><br />
              <span style={{ fontSize: 12 }}>(KB Set = ธุรกิจ 1 ร้าน)</span>
            </div>
          )}
          {biz.map((b) => (
            <div key={b.id} onClick={() => setSel(b.id)} style={{
              padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
              background: sel === b.id ? '#f3e8ff' : '#f9fafb', border: '1px solid ' + (sel === b.id ? '#c084fc' : '#f0f0f0'),
            }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{b.name}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                {MODES.find((m) => m.v === b.ai_mode)?.t.replace(/ \(.*\)| \(แนะนำ\)/, '') || b.ai_mode} · 📡 {(b.channel_ids || []).length} · 📄 {b.doc_count} · 🧩 {b.chunk_count}
              </div>
            </div>
          ))}
        </div>

        {/* detail */}
        <div style={{ flex: 1, minWidth: 340 }}>
          {!cfg ? (
            <div style={{ ...card, color: '#9ca3af' }}>เลือกธุรกิจทางซ้าย</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[['config', 'ตั้งค่า AI', Bot], ['knowledge', 'ความรู้', Database], ['unanswered', 'ตอบไม่ได้' + (unans.length ? ` (${unans.length})` : ''), AlertTriangle], ['usage', 'การใช้งาน', BarChart3]].map(([k, t, Ic]) => (
                  <button key={k} onClick={() => setTab(k)} style={{
                    padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    border: '1px solid ' + (tab === k ? '#7c3aed' : '#e5e7eb'),
                    background: tab === k ? '#7c3aed' : '#fff', color: tab === k ? '#fff' : '#374151',
                  }}><Ic size={15} /> {t}</button>
                ))}
              </div>

              {tab === 'config' && (
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>ธุรกิจ: {cfg.name}</h3>

                  {/* ai_channels_v1: which channels this business handles */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={lbl}>📡 ช่องทางที่ธุรกิจนี้ดูแล ({(cfg.channel_ids || []).length})</label>
                    {channels.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#9ca3af' }}>ยังไม่มีช่องทาง — เพิ่มที่เมนู Channels ก่อน</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {channels.map((ch) => {
                          const on = (cfg.channel_ids || []).includes(ch.id);
                          const owner = biz.find((x) => x.id !== sel && (x.channel_ids || []).includes(ch.id));
                          return (
                            <label key={ch.id} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8,
                              border: '1px solid ' + (on ? '#c084fc' : '#e5e7eb'), background: on ? '#faf5ff' : '#fff',
                              cursor: 'pointer', fontSize: 13,
                            }}>
                              <input type="checkbox" checked={on} onChange={() => {
                                const cur = cfg.channel_ids || [];
                                setCfgField('channel_ids', on ? cur.filter((x) => x !== ch.id) : [...cur, ch.id]);
                              }} />
                              {CH_ICON[ch.type] || '📡'} {ch.name}
                              {owner && !on && <span style={{ fontSize: 11, color: '#9ca3af' }}>(อยู่ใน {owner.name})</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>ข้อความจากช่องที่เลือกจะใช้ persona + ความรู้ของธุรกิจนี้ (กด "บันทึก" เพื่อยืนยัน)</div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={lbl}>โหมดการตอบ</label>
                    {MODES.map((m) => (
                      <label key={m.v} style={{
                        display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, borderRadius: 8, cursor: 'pointer',
                        border: '1px solid ' + (cfg.ai_mode === m.v ? '#c084fc' : '#e5e7eb'),
                        background: cfg.ai_mode === m.v ? '#faf5ff' : '#fff', marginBottom: 6,
                      }}>
                        <input type="radio" checked={cfg.ai_mode === m.v} onChange={() => setCfgField('ai_mode', m.v)} style={{ marginTop: 3 }} />
                        <div><div style={{ fontWeight: 700, fontSize: 14 }}>{m.t}</div><div style={{ fontSize: 12, color: '#6b7280' }}>{m.d}</div></div>
                      </label>
                    ))}
                  </div>

                  {(cfg.ai_mode === 'rag' || cfg.ai_mode === 'llm') && (
                    <>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 150 }}>
                          <label style={lbl}>ผู้ให้บริการ AI</label>
                          <select style={inp} value={cfg.ai_provider || ''} onChange={(e) => { const p = e.target.value; setCfg((c) => ({ ...c, ai_provider: p, ai_chat_model: modelsFor(p)[0]?.model || '' })); }}>
                            <option value="">— เลือก —</option>
                            {providers.map((p) => <option key={p} value={p}>{PROV_LABEL[p] || p}</option>)}
                          </select>
                        </div>
                        <div style={{ flex: 1, minWidth: 150 }}>
                          <label style={lbl}>โมเดล</label>
                          <select style={inp} value={cfg.ai_chat_model || ''} onChange={(e) => setCfgField('ai_chat_model', e.target.value)}>
                            <option value="">— เลือก —</option>
                            {modelsFor(cfg.ai_provider).map((m) => <option key={m.model} value={m.model}>{m.model}</option>)}
                          </select>
                        </div>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <label style={lbl}>บุคลิก / คำสั่งระบบ (persona)</label>
                        <textarea style={{ ...inp, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                          placeholder="เช่น คุณเป็นแอดมินร้าน... ตอบสุภาพ เป็นกันเอง ใช้คำลงท้าย ค่ะ"
                          value={cfg.ai_prompt || ''} onChange={(e) => setCfgField('ai_prompt', e.target.value)} />
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <label style={lbl}>🚨 หัวข้อที่ต้องแจ้งแอดมิน (แม้ AI ตอบได้)</label>
                        <input style={inp} placeholder="เช่น ถอนเงิน, คืนเงิน, ร้องเรียน, โอนผิด (คั่นด้วยจุลภาค)"
                          value={cfg.ai_alert_topics || ''} onChange={(e) => setCfgField('ai_alert_topics', e.target.value)} />
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>ลูกค้าถามเรื่องเหล่านี้ → AI ตอบตามปกติ + แจ้งเตือน Telegram ให้แอดมินเข้ามาดูแลต่อ</div>
                      </div>

                      {/* ai_deescalation_v1 */}
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                          <input type="checkbox" checked={cfg.ai_deescalation !== false}
                            onChange={(e) => setCfgField('ai_deescalation', e.target.checked)} />
                          🛡️ รับมือคำหยาบ / ลูกค้าโมโห (de-escalation)
                        </label>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                          เปิด: ลูกค้าพิมพ์คำหยาบหรือโมโห → AI ตอบสุภาพใจเย็น ไม่โต้เถียง ไม่ด่ากลับ และถ้าหยาบรุนแรงจะเติมแจ้งเตือนให้เจ้าหน้าที่เข้ามารับช่วงอัตโนมัติ
                        </div>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <label style={lbl}>ข้อความเมื่อ AI ตอบไม่ได้ (fallback)</label>
                        <input style={inp} placeholder="เช่น ขอเวลาสักครู่นะคะ เดี๋ยวเจ้าหน้าที่ติดต่อกลับค่ะ"
                          value={cfg.ai_fallback_answer || ''} onChange={(e) => setCfgField('ai_fallback_answer', e.target.value)} />
                      </div>

                      <div style={{ marginBottom: 16, maxWidth: 220 }}>
                        <label style={lbl}>ความสร้างสรรค์ (temperature): {cfg.ai_temperature == null ? 0.3 : cfg.ai_temperature}</label>
                        <input type="range" min="0" max="1" step="0.1" style={{ width: '100%' }}
                          value={cfg.ai_temperature == null ? 0.3 : cfg.ai_temperature}
                          onChange={(e) => setCfgField('ai_temperature', parseFloat(e.target.value))} />
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>ต่ำ = ตรงข้อมูล · สูง = ยืดหยุ่น</div>
                      </div>

                      {cfg.ai_mode === 'rag' && docs.length === 0 && (
                        <div style={{ padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 16 }}>
                          ⚠️ โหมดนี้ต้องมีความรู้ก่อน — ไปที่แท็บ "ความรู้" เพื่อเพิ่มข้อมูลธุรกิจ
                        </div>
                      )}
                    </>
                  )}

                  <button style={btn('#7c3aed', saving)} disabled={saving} onClick={save}>
                    {saving ? <Loader size={16} className="spin" /> : <Save size={16} />} บันทึก
                  </button>
                </div>
              )}

              {tab === 'knowledge' && (
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>ฐานความรู้ของ {cfg.name}</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>วางข้อมูลธุรกิจ (เวลาเปิด-ปิด, สินค้า, ราคา, เงื่อนไข ฯลฯ) AI จะใช้ตอบเฉพาะธุรกิจนี้</p>
                  <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>📋 ทั่วไป:</span>
                    {TEMPLATES.map((t) => (
                      <button key={t.title} onClick={() => applyTemplate(t)} style={{
                        padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        border: '1px solid #c084fc', color: '#7c3aed', background: '#faf5ff',
                      }}>{t.short}</button>
                    ))}
                  </div>
                  {/* kb_template_game_v1: vertical pack from real top questions */}
                  <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>🎰 เว็บเกม/เติมเกม:</span>
                    {TEMPLATES_GAME.map((t) => (
                      <button key={t.title} onClick={() => applyTemplate(t)} style={{
                        padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        border: '1px solid #f59e0b', color: '#b45309', background: '#fffbeb',
                      }}>{t.short}</button>
                    ))}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    <input style={{ ...inp, marginBottom: 8 }} placeholder="หัวข้อ (เช่น ข้อมูลทั่วไป, เมนู, โปรโมชั่น)"
                      value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} />
                    <textarea style={{ ...inp, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
                      placeholder="วางเนื้อหาความรู้ที่นี่..."
                      value={newDoc.text} onChange={(e) => setNewDoc({ ...newDoc, text: e.target.value })} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      {newDoc.image_url ? (
                        <>
                          <img src={newDoc.image_url} alt="แนบ" style={{ height: 44, borderRadius: 6, border: '1px solid #e5e7eb' }} />
                          <button onClick={() => setNewDoc({ ...newDoc, image_url: '' })}
                            style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#9ca3af', fontSize: 12 }}>เอารูปออก</button>
                        </>
                      ) : (
                        <label style={{ fontSize: 13, color: '#0ea5e9', cursor: 'pointer', fontWeight: 600 }}>
                          {imgUploading ? '⏳ กำลังอัปโหลด...' : '🖼️ แนบรูป (AI จะส่งรูปนี้คู่คำตอบเมื่อใช้ความรู้นี้)'}
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={uploadDocImage} disabled={imgUploading} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button style={btn(editDocId ? '#f59e0b' : '#16a34a', ingesting)} disabled={ingesting} onClick={addDoc}>
                      {ingesting ? <><Loader size={16} className="spin" /> กำลังประมวลผล...</>
                        : editDocId ? <><Save size={16} /> บันทึกการแก้ไข</>
                        : <><Plus size={16} /> เพิ่มความรู้</>}
                    </button>
                    {editDocId && (
                      <button onClick={cancelEdit} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', cursor: 'pointer', color: '#6b7280', fontSize: 13 }}>
                        ยกเลิกการแก้ไข
                      </button>
                    )}
                  </div>

                  {/* ai_ingest_v1: pull from web / file */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center', paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
                    <input style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="หรือวางลิงก์เว็บ เช่น https://ร้านของคุณ.com"
                      value={ingestUrl} onChange={(e) => setIngestUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !urlLoading) fetchFromUrl(); }} />
                    <button style={btn('#0ea5e9', urlLoading)} disabled={urlLoading} onClick={fetchFromUrl}>
                      {urlLoading ? <Loader size={15} /> : '🌐'} ดึงจากเว็บ
                    </button>
                    <label style={{ ...btn('#64748b', fileLoading), cursor: fileLoading ? 'wait' : 'pointer' }}>
                      {fileLoading ? <Loader size={15} /> : '📄'} อัปโหลด PDF/TXT
                      <input type="file" accept=".pdf,.txt" style={{ display: 'none' }} onChange={uploadKbFile} disabled={fileLoading} />
                    </label>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    {docs.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>ยังไม่มีความรู้</div>}
                    {docs.map((d) => (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{d.image_url && '🖼️ '}{d.title}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>🧩 {d.chunk_count} ชิ้น · {nf(d.char_count)} ตัวอักษร · {new Date(d.created_at).toLocaleDateString('th-TH')}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => editDoc(d)} title="แก้ไข" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0ea5e9' }}><Edit2 size={17} /></button>
                          <button onClick={() => delDoc(d.id)} title="ลบ" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={18} /></button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ai_gallery_v1: random image gallery -> Flex carousel */}
                  <div style={{ marginTop: 22, paddingTop: 14, borderTop: '1px dashed #e5e7eb' }}>
                    <div style={{ ...lbl, marginBottom: 2 }}>🎲 คลังรูปสุ่ม (ส่งเป็นการ์ด Flex)</div>
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: '2px 0 10px' }}>
                      เมื่อลูกค้า "ขอดูรูป/สินค้า/ตัวอย่าง" AI จะสุ่มรูปจากคลังนี้ส่งเป็นการ์ดเลื่อนดูได้อัตโนมัติ
                    </p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13 }}>ส่งครั้งละ</span>
                      <input type="number" min="1" max="10" style={{ ...inp, width: 64 }}
                        value={cfg.ai_gallery_count || 3}
                        onChange={(e) => setCfgField('ai_gallery_count', e.target.value)} />
                      <span style={{ fontSize: 13 }}>รูป</span>
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึกจำนวน</button>
                      <input style={{ ...inp, flex: 1, minWidth: 140 }} placeholder='ชื่อรูป (ไม่บังคับ เช่น เสื้อรุ่น A)'
                        value={galTitle} onChange={(e) => setGalTitle(e.target.value)} />
                      <label style={{ ...btn('#16a34a', galUploading), cursor: galUploading ? 'wait' : 'pointer', padding: '8px 14px', fontSize: 13 }}>
                        {galUploading ? <Loader size={14} /> : '➕'} เพิ่มรูป (เลือกหลายรูปได้)
                        <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={uploadGalleryImg} disabled={galUploading} />
                      </label>
                    </div>
                    {/* ai_gallery_cta_v1: CTA button + default link for all cards */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>🔘 ปุ่มบนการ์ด:</span>
                      <input style={{ ...inp, width: 160 }} placeholder="ข้อความปุ่ม เช่น เล่นเลย 🎮"
                        value={cfg.ai_gallery_cta || ''} onChange={(e) => setCfgField('ai_gallery_cta', e.target.value)} maxLength={20} />
                      <input style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="ลิงก์หลัก https://... (ใช้กับทุกการ์ด)"
                        value={cfg.ai_gallery_link || ''} onChange={(e) => setCfgField('ai_gallery_link', e.target.value)} />
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึกปุ่ม/ลิงก์</button>
                    </div>
                    {/* ai_gallery_badge_v1: random-percent badge on cards */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>📊 แถบเปอร์เซ็นต์:</span>
                      <input style={{ ...inp, flex: 1, minWidth: 170 }}
                        placeholder="ข้อความ เช่น ลูกค้าซื้อซ้ำ / 🔥 แตกแล้ว {'{n}'}% (เว้นว่าง = ไม่แสดง)"
                        value={cfg.ai_gallery_badge_text || ''}
                        onChange={(e) => setCfgField('ai_gallery_badge_text', e.target.value)} />
                      <span style={{ fontSize: 13 }}>สุ่ม</span>
                      <input type="number" min="0" max="100" style={{ ...inp, width: 62 }}
                        value={cfg.ai_gallery_pct_min == null ? 90 : cfg.ai_gallery_pct_min}
                        onChange={(e) => setCfgField('ai_gallery_pct_min', e.target.value)} />
                      <span style={{ fontSize: 13 }}>–</span>
                      <input type="number" min="0" max="100" style={{ ...inp, width: 62 }}
                        value={cfg.ai_gallery_pct_max == null ? 98 : cfg.ai_gallery_pct_max}
                        onChange={(e) => setCfgField('ai_gallery_pct_max', e.target.value)} />
                      <span style={{ fontSize: 13 }}>%</span>
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึก</button>
                    </div>
                    {/* ai_gallery_card_v2: คำโปรยใต้ชื่อเกม + ช่องสถิติเพิ่มอีก 2 ช่อง
                        เป็นข้อความอิสระ ระบบไม่มีตัวเลขจริงพวกนี้ จะคำนวณให้เองไม่ได้
                        เว้นว่าง = ไม่แสดงช่องนั้น ไม่ใช่แสดงเป็นช่องเปล่า */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>📝 คำโปรยใต้ชื่อ:</span>
                      <input style={{ ...inp, flex: 1, minWidth: 220 }} maxLength={120}
                        placeholder="เช่น สูตรมาจากสถิติลูกค้าเล่นจริง (เว้นว่าง = ไม่แสดง)"
                        value={cfg.ai_gallery_subtitle || ''}
                        onChange={(e) => setCfgField('ai_gallery_subtitle', e.target.value)} />
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึก</button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>📈 ช่องสถิติเพิ่ม:</span>
                      <input style={{ ...inp, width: 120 }} maxLength={40} placeholder="ชื่อ เช่น เข้าฟรีสปิน"
                        value={cfg.ai_gallery_stat2_label || ''}
                        onChange={(e) => setCfgField('ai_gallery_stat2_label', e.target.value)} />
                      <input style={{ ...inp, width: 100 }} maxLength={40} placeholder="ค่า เช่น x14000"
                        value={cfg.ai_gallery_stat2_value || ''}
                        onChange={(e) => setCfgField('ai_gallery_stat2_value', e.target.value)} />
                      <input style={{ ...inp, width: 120 }} maxLength={40} placeholder="ชื่อ เช่น ตัวคูณสูงสุด"
                        value={cfg.ai_gallery_stat3_label || ''}
                        onChange={(e) => setCfgField('ai_gallery_stat3_label', e.target.value)} />
                      <input style={{ ...inp, width: 100 }} maxLength={40} placeholder="ค่า เช่น 28%"
                        value={cfg.ai_gallery_stat3_value || ''}
                        onChange={(e) => setCfgField('ai_gallery_stat3_value', e.target.value)} />
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึก</button>
                    </div>
                    {/* gallery_trust_row_v1: แถวโลโก้ท้ายการ์ด 3 ช่อง
                        ⚠️ โลโก้ต้องเป็น PNG พื้นโปร่ง เพราะการ์ดพื้นดำ
                        ถ้าไฟล์มีพื้นขาวติดมาจะเห็นเป็นกล่องขาวกลางการ์ด */}
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>🏅 แถวโลโก้ท้ายการ์ด</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, lineHeight: 1.6 }}>
                        ใช้ทำแถวแบบ "ใบอนุญาต / แบรนด์ / ภาษารับรอง" · เว้นว่าง = ไม่แสดงช่องนั้น ·
                        โลโก้ควรเป็น <b>PNG พื้นโปร่ง</b> เพราะการ์ดพื้นดำ
                      </div>
                      {[1, 2, 3].map((slot) => (
                        <div key={slot} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, color: '#6b7280', width: 44 }}>ช่อง {slot}</span>
                          <label style={{ ...btn('#0ea5e9', trustUploading === slot), padding: '6px 10px', fontSize: 12, cursor: 'pointer' }}>
                            {trustUploading === slot ? 'กำลังอัป...' : '📤 เลือกรูป'}
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                              onChange={(ev) => uploadTrustLogo(slot, ev)} disabled={trustUploading === slot} />
                          </label>
                          <input style={{ ...inp, flex: 1, minWidth: 160 }} placeholder="URL รูป (อัปโหลดแล้วจะเติมให้เอง)"
                            value={cfg['ai_gallery_trust' + slot + '_url'] || ''}
                            onChange={(ev) => setCfgField('ai_gallery_trust' + slot + '_url', ev.target.value)} />
                          <input style={{ ...inp, width: 140 }} maxLength={40} placeholder="คำใต้รูป เช่น ใบอนุญาต"
                            value={cfg['ai_gallery_trust' + slot + '_label'] || ''}
                            onChange={(ev) => setCfgField('ai_gallery_trust' + slot + '_label', ev.target.value)} />
                          {cfg['ai_gallery_trust' + slot + '_url'] && (
                            <span style={{ background: '#0B0B0F', padding: 4, borderRadius: 6, display: 'inline-flex' }}>
                              <img src={cfg['ai_gallery_trust' + slot + '_url']} alt="" style={{ height: 24, display: 'block' }} />
                            </span>
                          )}
                        </div>
                      ))}
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึกแถวโลโก้</button>
                    </div>
                    {/* ai_gallery_topics_v1: owner words that always trigger the gallery */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>🎯 คำที่ให้ส่งรูปเสมอ:</span>
                      <input style={{ ...inp, flex: 1, minWidth: 200 }}
                        placeholder="เช่น เกมแตก, แนะนำเกม, โปรวันนี้ (คั่นด้วยจุลภาค — เจอคำพวกนี้ = ส่งรูปทันที)"
                        value={cfg.ai_gallery_topics || ''}
                        onChange={(e) => setCfgField('ai_gallery_topics', e.target.value)} />
                      <button style={{ ...btn('#7c3aed', saving), padding: '7px 12px', fontSize: 13 }} disabled={saving} onClick={save}>บันทึกคำ</button>
                    </div>
                    {galleryImgs.length === 0 ? (
                      <div style={{ color: '#9ca3af', fontSize: 13 }}>ยังไม่มีรูปในคลัง</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {galleryImgs.map((g) => (
                          <div key={g.id} style={{ position: 'relative', width: 92 }}>
                            <img src={g.image_url} alt={g.title || ''} style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                            <button onClick={() => delGalleryImg(g.id)} style={{
                              position: 'absolute', top: 2, right: 2, background: 'rgba(220,38,38,0.9)', color: '#fff',
                              border: 'none', borderRadius: 6, width: 20, height: 20, cursor: 'pointer', fontSize: 11, lineHeight: '1',
                            }}>✕</button>
                            <button onClick={() => editGalleryLink(g)} title={g.link_url || 'ตั้งลิงก์เฉพาะรูปนี้'} style={{
                              position: 'absolute', top: 2, left: 2, background: g.link_url ? 'rgba(14,165,233,0.95)' : 'rgba(100,116,139,0.8)', color: '#fff',
                              border: 'none', borderRadius: 6, width: 20, height: 20, cursor: 'pointer', fontSize: 11, lineHeight: '1',
                            }}>🔗</button>
                            {g.title && <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.title}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === 'usage' && (
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>การใช้งาน 30 วันล่าสุด (ทั้งบัญชี)</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}>
                      <th style={{ padding: 8 }}>ธุรกิจ</th><th style={{ padding: 8 }}>ครั้ง</th><th style={{ padding: 8 }}>tokens (in/out)</th><th style={{ padding: 8, textAlign: 'right' }}>โทเคนที่ใช้</th>
                    </tr></thead>
                    <tbody>
                      {usage.length === 0 && <tr><td colSpan={4} style={{ padding: 12, color: '#9ca3af' }}>ยังไม่มีการใช้งาน</td></tr>}
                      {usage.map((u, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                          <td style={{ padding: 8, fontWeight: 600 }}>{u.business}</td>
                          <td style={{ padding: 8 }}>{nf(u.calls)}</td>
                          <td style={{ padding: 8, color: '#6b7280' }}>{nf(u.input_tokens)} / {nf(u.output_tokens)}</td>
                          <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{nf(u.tokens_charged)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'unanswered' && (
                <div style={card}>
                  <h3 style={{ marginTop: 0 }}>❓ คำถามที่ AI ตอบไม่ได้ ({unans.length})</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>กด "เพิ่มเป็นความรู้" แล้วพิมพ์คำตอบ — ครั้งหน้าบอทจะตอบเองได้ทันที</p>
                  {unans.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>ไม่มีค้าง 🎉 บอทตอบได้ทุกคำถามที่ผ่านมา</div>}
                  {unans.map((u) => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{u.question}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          ถูกถาม <b>{u.hits}</b> ครั้ง · ล่าสุด {new Date(u.last_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                        </div>
                      </div>
                      <button style={{ ...btn('#16a34a'), padding: '8px 12px', fontSize: 13 }} onClick={() => addAsKnowledge(u)}>
                        <Plus size={14} /> เพิ่มเป็นความรู้
                      </button>
                      <button onClick={() => dismissUnans(u.id)}
                        style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#9ca3af', fontSize: 13 }}>
                        ซ่อน
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* ai_test_v1: playground — try the bot with real settings/knowledge */}
              <div style={{ ...card, marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>🧪 ทดสอบบอท ({cfg.name})</h3>
                  {testMsgs.length > 0 && (
                    <button onClick={() => setTestMsgs([])} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 12 }}>ล้างบทสนทนา</button>
                  )}
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0 8px' }}>ถามเหมือนเป็นลูกค้า — ใช้การตั้งค่า/ความรู้จริง (ใช้โทเคนจริง, ไม่ส่งถึงลูกค้า)</p>
                {testMsgs.length > 0 && (
                  <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                    {testMsgs.map((m, i) => (
                      <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                        <div style={{
                          padding: '8px 12px', borderRadius: 12, fontSize: 14, whiteSpace: 'pre-wrap',
                          background: m.role === 'user' ? '#7c3aed' : '#f3f4f6', color: m.role === 'user' ? '#fff' : '#111827',
                        }}>{m.text}</div>
                        {m.role === 'bot' && m.source && (
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                            {({ rag: '📚 จากฐานความรู้', cache: '⚡ จากแคช (ฟรี)', llm: '🧠 AI อิสระ', no_answer: '❌ ตอบไม่ได้', keyword: '🔑 คีย์เวิร์ด', none: '🤐 ไม่ตอบ', error: '⚠️ ผิดพลาด' })[m.source] || m.source}
                            {m.alert && ' · 🚨 แจ้งแอดมิน'}
                            {/* kb_match_trace_v1: บอกว่าคำตอบมาจากรายการไหน พร้อมทางไปแก้
                                เดิมบอกแค่ว่า "มาจากคีย์เวิร์ด" ซึ่งไม่พอจะหาเจอ
                                ร้านที่ตั้งคำตอบไว้เป็นร้อยรายการต้องไล่เปิดทีละอันเอง */}
                            {m.matchedKb && (
                              <> · <a href={`/bot-training?edit=${m.matchedKb.id}`} style={{ color: '#7c3aed', textDecoration: 'underline' }}>
                                แก้คำตอบนี้{m.matchedKb.topic ? ` (${m.matchedKb.topic})` : ''}
                              </a></>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {testing && <div style={{ alignSelf: 'flex-start', fontSize: 13, color: '#9ca3af' }}>กำลังคิด...</div>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inp, flex: 1 }} placeholder="พิมพ์คำถามทดสอบ แล้วกด Enter..." value={testQ}
                    onChange={(e) => setTestQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runTest(); }} />
                  <button style={btn('#7c3aed', testing || !testQ.trim())} disabled={testing || !testQ.trim()} onClick={runTest}>
                    {testing ? <Loader size={16} /> : <Send size={16} />} ถาม
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showBuy && (
        <div onClick={() => setShowBuy(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 20, overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ ...card, maxWidth: 640, width: '100%', marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 20 }}><Wallet size={22} color="#7c3aed" /> เติมโทเคน AI</h2>
              <button onClick={() => setShowBuy(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
            </div>

            <div style={lbl}>เลือกแพ็กเกจ</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 18 }}>
              {pkgs.map((p) => {
                const total = Number(p.tokens) + Number(p.bonus_tokens || 0);
                const on = buyPkg && buyPkg.id === p.id;
                return (
                  <div key={p.id} onClick={() => setBuyPkg(p)} style={{
                    padding: 14, borderRadius: 10, cursor: 'pointer', textAlign: 'center', position: 'relative',
                    border: '2px solid ' + (on ? '#7c3aed' : '#e5e7eb'), background: on ? '#faf5ff' : '#fff',
                  }}>
                    {on && <Check size={16} color="#7c3aed" style={{ position: 'absolute', top: 8, right: 8 }} />}
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{p.name}</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#7c3aed', margin: '6px 0' }}>฿{nf(p.price_thb)}</div>
                    <div style={{ fontSize: 12, color: '#374151' }}>{nf(total)} โทเคน</div>
                    {p.bonus_tokens > 0 && <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>+ โบนัส {nf(p.bonus_tokens)}</div>}
                  </div>
                );
              })}
            </div>

            {buyPkg && (
              <>
                <div style={lbl}><CreditCard size={14} style={{ verticalAlign: -2 }} /> โอนเงิน ฿{nf(buyPkg.price_thb)} มาที่บัญชี</div>
                {banks.length === 0 ? (
                  <div style={{ padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 14 }}>ยังไม่มีบัญชีรับชำระ กรุณาติดต่อแอดมิน</div>
                ) : (
                  <div style={{ marginBottom: 14 }}>
                    {banks.map((b, i) => (
                      <div key={i} style={{ padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 6, background: '#f9fafb' }}>
                        <div style={{ fontWeight: 700 }}>{b.bank_name} {b.account_number ? '· ' + b.account_number : ''}</div>
                        {b.account_name && <div style={{ fontSize: 13, color: '#6b7280' }}>{b.account_name}</div>}
                        {b.promptpay_id && <div style={{ fontSize: 13 }}>PromptPay: <span style={{ fontFamily: 'monospace' }}>{b.promptpay_id}</span></div>}
                      </div>
                    ))}
                  </div>
                )}

                <div style={lbl}>อัปโหลดสลิปการโอน</div>
                <label style={{ display: 'block', padding: 20, border: '2px dashed ' + (slip ? '#22c55e' : '#cbd5e1'), borderRadius: 10, textAlign: 'center', cursor: 'pointer', marginBottom: 14, background: slip ? '#f0fdf4' : '#fafafa' }}>
                  <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={(e) => setSlip(e.target.files[0])} />
                  <Upload size={22} color={slip ? '#22c55e' : '#94a3b8'} />
                  <div style={{ fontSize: 13, marginTop: 6, color: slip ? '#16a34a' : '#6b7280' }}>{slip ? slip.name : 'คลิกเพื่อเลือกสลิป (jpg, png, pdf)'}</div>
                </label>

                <button style={{ ...btn('#7c3aed', buying), width: '100%', justifyContent: 'center' }} disabled={buying} onClick={submitBuy}>
                  {buying ? <Loader size={16} /> : <Check size={16} />} ยืนยันการซื้อ ฿{nf(buyPkg.price_thb)}
                </button>
                <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 8 }}>โทเคนจะเข้ากระเป๋าหลังแอดมินอนุมัติสลิป</div>
              </>
            )}

            {orders.length > 0 && (
              <div style={{ marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
                <div style={lbl}>ประวัติคำสั่งซื้อ</div>
                {orders.map((o) => {
                  const st = { pending: ['รออนุมัติ', '#92400e', '#fef3c7'], approved: ['สำเร็จ', '#166534', '#dcfce7'], rejected: ['ปฏิเสธ', '#991b1b', '#fee2e2'] }[o.status] || [o.status, '#475569', '#f1f5f9'];
                  return (
                    <div key={o.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 13 }}>
                      <div>{o.package_name} · {nf(o.tokens_granted)} โทเคน · ฿{nf(o.amount)}</div>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700, color: st[1], background: st[2] }}>{st[0]}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
